import React, { useEffect, useState, useRef } from "react"
import { useParams } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { useTranslation } from "react-i18next"
import { jwtDecode } from "jwt-decode"
import Loading from "../../components/Loading/Loading"
import RessourcesPage from "../Ressources/RessourcesPage"
import HowToRead from "../../components/HowToRead/HowToRead"
import gommettesDefinition from "../../utils/gommettes2025.json"
import "./Report.css"

const GOMMET_GREEN = encodeURI("/gommetteGreen.svg")
const GOMMET_RED = encodeURI("/gommetteRed.svg")

if (typeof window !== "undefined") {
  try {
    const searchParams = new URLSearchParams(window.location.search)
    const printToken = searchParams.get("printToken")

    if (printToken) {
      localStorage.setItem("auth_token", printToken)
      sessionStorage.setItem("auth_token", printToken)
      window.__puppeteerAuth = true
      console.log("[Report2025] printToken détecté — mode Puppeteer activé")
    }
  } catch (e) {
    console.warn("[Report2025] Erreur lecture printToken:", e)
  }

  const t = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")
  if (t) {
    try {
      const { exp } = JSON.parse(atob(t.split(".")[1]))
      if (Date.now() < exp * 1000) {
        window.__puppeteerAuth = true
      } else {
        localStorage.removeItem("auth_token")
        sessionStorage.removeItem("auth_token")
      }
    } catch {}
  }
}

const customStyles2025 = {
  responseTable: {
    borderSpacing: "16px 0"
  },
  thScore: {
    width: "90px"
  },
  tdScore: {
    width: "90px"
  },
  gommetIcon: {
    width: "16px",
    height: "16px"
  }
}

const Report2025 = () => {
  const { sessionId } = useParams()
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0()
  const { t, i18n } = useTranslation()
  const contentRef = useRef()

  const [session, setSession] = useState(null)
  const [formDefinition, setFormDefinition] = useState([])
  const [isLoading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const normalize = (str) =>
    (str || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/['’‘‛`´]/g, "'")
      .replace(/[^\w\s€']/g, "")
      .replace(/€/g, "euro")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()

  const normalizeKey = (key) =>
    normalize(
      (key || "")
        .replace(/\[\]/g, "")
        .replace(/\[\d+\]/g, "")
    ).replace(/[^a-z0-9_]/g, "")

  const suffixOf = (key) =>
    normalize(
      (key || "")
        .replace(/\[\]/g, "")
        .replace(/\[\d+\]/g, "")
    )
      .replace(/^q\d+_/i, "")
      .replace(/[^a-z0-9_]/g, "")

  const getGommettesMappingFor = (baseKey) => {
    if (!baseKey) return null

    const nk = normalizeKey(baseKey)
    const sx = suffixOf(baseKey)

    let hit = Object.keys(gommettesDefinition).find(k => normalizeKey(k) === nk)
    if (hit) return gommettesDefinition[hit]

    hit = Object.keys(gommettesDefinition).find(k => suffixOf(k) === sx)
    if (hit) return gommettesDefinition[hit]

    const numMatch = baseKey.match(/^q(\d+)_/)
    if (numMatch) {
      const qNum = numMatch[1]
      hit = Object.keys(gommettesDefinition).find(k =>
        k.match(new RegExp(`^q\\d*_?${qNum}`))
      )
      if (hit) return gommettesDefinition[hit]
    }

    hit = Object.keys(gommettesDefinition).find(k =>
      suffixOf(k).startsWith(sx) || sx.startsWith(suffixOf(k))
    )
    if (hit) return gommettesDefinition[hit]

    return null
  }

  useEffect(() => {
    const token =
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token")

    if (token && !isAuthenticated) {
      try {
        const decoded = jwtDecode(token)
        const exp = decoded.exp * 1000
        if (Date.now() < exp) {
          window.__puppeteerAuth = true
        } else {
          console.warn("[Report2025] Token expiré — suppression du storage")
          localStorage.removeItem("auth_token")
          sessionStorage.removeItem("auth_token")
        }
      } catch (e) {
        console.warn("[Report2025] Erreur décodage token local", e)
      }
    }

    if (!isAuthenticated && !token) {
      console.log("[Report2025] Aucun token — redirection Auth0")
      loginWithRedirect()
    }
  }, [isAuthenticated, loginWithRedirect])

  useEffect(() => {
    fetch("/Form/survey_2025.json")
      .then(res => res.json())
      .then(data => setFormDefinition(data))
      .catch(err => {
        console.error("Erreur chargement formulaire 2025:", err)
        setError("Erreur de chargement du formulaire 2025")
      })
  }, [])

  useEffect(() => {
    ;(async () => {
      await i18n.changeLanguage("fr")
      try {
        if (!sessionId) throw new Error("Paramètre :sessionId manquant")

        const localToken =
          localStorage.getItem("auth_token") ||
          sessionStorage.getItem("auth_token")

        const token = window.__puppeteerAuth
          ? localToken
          : await getAccessTokenSilently()

        const baseUrl = process.env.REACT_APP_API_BASE_URL || ""
        const url = `${baseUrl}/surveySession/${sessionId}`

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json"
          }
        })

        const text = await response.text()
        if (!response.ok) throw new Error(`Erreur API: ${response.status}`)

        const data = JSON.parse(text)
        setSession(data)
      } catch (err) {
        console.error("Erreur chargement SurveySession:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    })()
  }, [sessionId, getAccessTokenSilently, i18n])

  if (!isAuthenticated && !window.__puppeteerAuth) {
    return <Loading />
  }

  if (isLoading || !formDefinition.length) return <Loading />
  if (error) {
    return (
      <div className="print-container">
        <div className="page text-center">
          <h2>Erreur de chargement du rapport 2025</h2>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  if (!session) return <div>Aucun résultat trouvé.</div>

  const enterprise = session.enterprise || {}
  const responses = session.responses || {}

  const formatQuestionTitle = (question, index) => {
    const text = (question?.text || question?.name || "").trim()
    return /^\d+\)/.test(text) ? text : `${index}) ${text}`
  }

  const renderResponses = () => {
    let index = 0

    return formDefinition
      .map((section, sectionIdx) => {
        const rowsForSection = section.questions
          .map((question, qIdx) => {
            index++
            const name = question.name
            const baseKey = `q${question.qid}_${name}`

            const matchingKeys = Object.keys(responses).filter(
              key => key === baseKey || key.startsWith(baseKey + "[")
            )

            let selectedValues = []
            matchingKeys.forEach(key => {
              const value = responses[key]
              if (Array.isArray(value)) selectedValues.push(...value)
              else if (typeof value === "string" && value.trim() !== "")
                selectedValues.push(value.trim())
            })
            selectedValues = [...new Set(selectedValues)]

            if (
              selectedValues.length === 0 &&
              question.type !== "control_matrix" &&
              !responses[baseKey]
            ) {
              return null
            }

            const mapping = getGommettesMappingFor(baseKey) || {}

           const questionTitleRow = (
              <tr
                key={`q-title-${sectionIdx}-${qIdx}`}
                className="tr-question"
              >
                <td className="td-score axis-scoring"></td>
                <td className="td-score axis-scoring"></td>
                <td className="td-score axis-scoring"></td>
                <td className="td-score axis-scoring"></td>
                <td>
                  <p className="question">
                    {formatQuestionTitle(question, index)}
                  </p>
                </td>
              </tr>
            )
            const renderMatrix = () => {
              if (question.type !== "control_matrix" || !question.matrix) return null;

              const { rows = [], cols = [] } = question.matrix;
              const matrixBaseKey = `q${question.qid}_${question.name}`;
              const toLetter = (i) => String.fromCharCode(65 + i);

              return rows.map((rowLabel, rIdx) => {
                const answerKey = `${matrixBaseKey}[${rIdx}]`;
                const userAnswer = responses[answerKey];
                const normalizedAnswer = normalize(userAnswer);

                const mapping = gommettesDefinition[answerKey] || {};
                const axes = mapping.axes || [1, 2, 3, 4];

                const emoji =
                  Object.entries(mapping.answers || {}).find(
                    ([k]) => normalize(k) === normalizedAnswer
                  )?.[1] || "";

                const showGreen = emoji === "🟢";
                const showRed = emoji === "🔴";

                let explanationText = "";

                if (showRed) {
                  if (
                    typeof mapping.explanation === "string" &&
                    mapping.explanation.trim() !== ""
                  ) {
                    explanationText = mapping.explanation.trim();
                  } else if (mapping.answers) {
                    for (const [, value] of Object.entries(mapping.answers)) {
                      if (typeof value === "string") {
                        const trimmed = value.trim();
                        if (
                          trimmed &&
                          !trimmed.includes("🟢") &&
                          !trimmed.includes("🔴")
                        ) {
                          explanationText = trimmed;
                          break;
                        }
                      }
                    }
                  }
                }

                return (
                  <React.Fragment key={`matrix-${sectionIdx}-${qIdx}-${rIdx}`}>

                    <tr key={`matrix-title-${sectionIdx}-${qIdx}-${rIdx}`} className="tr-question">
                      <td className="td-score axis-scoring"></td>
                      <td className="td-score axis-scoring"></td>
                      <td className="td-score axis-scoring"></td>
                      <td className="td-score axis-scoring"></td>
                      <td
                        className="question"
                        style={{ fontWeight: "bold", color: "#0A93D2" }}
                      >
                        {`${toLetter(rIdx)}. ${rowLabel}`}
                      </td>
                    </tr>

                    {cols.map((col, cIdx) => {
                      const isSelected = normalizedAnswer === normalize(col);

                      return (
                        <tr key={`matrix-choice-${sectionIdx}-${qIdx}-${rIdx}-${cIdx}`}>
                          {[1, 2, 3, 4].map((axis) => (
                            <td key={`axis-${axis}`} className="td-score axis-scoring">
                              {isSelected && axes.includes(axis) && showGreen && (
                                <img
                                  src={GOMMET_GREEN}
                                  alt="🟢"
                                  className="gomet-icon"
                                  style={customStyles2025.gommetIcon}
                                />
                              )}

                              {isSelected && axes.includes(axis) && showRed && (
                                <img
                                  src={GOMMET_RED}
                                  alt="🔴"
                                  className="gomet-icon"
                                  style={customStyles2025.gommetIcon}
                                />
                              )}
                            </td>
                          ))}

                          <td
                            className="answered"
                            style={{
                              color: isSelected ? "#0A93D2" : "#000",
                              fontWeight: isSelected ? "bold" : "normal"
                            }}
                          >
                            {`${cIdx + 1}. ${col}`}
                          </td>
                        </tr>
                      );
                    })}

                    {showRed && explanationText && (
                      <tr key={`matrix-expl-${sectionIdx}-${qIdx}-${rIdx}`}>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td
                          style={{
                            color: "#D32F2F",
                            fontSize: "12px",
                            paddingTop: "4px",
                            paddingBottom: "8px",
                            fontStyle: "italic"
                          }}
                        >
                          {explanationText}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              });
            };

            const renderChoiceRows = () => {
              if (!Array.isArray(question.choices)) return null;

              const normalizedSelected = selectedValues.map(v => normalize(v));
              const otherKey = `${baseKey}[other]`;
              const otherValue = responses[otherKey];

              const rows = question.choices.map((choice, cIdx) => {
                const normChoice = normalize(choice);
                const isOther = normChoice.startsWith("autre");
                const hasOtherValue = !!otherValue && otherValue.trim() !== "";

                const isSelected =
                  normalizedSelected.includes(normChoice) ||
                  (isOther && hasOtherValue);

                const label =
                  isOther && hasOtherValue
                    ? `${choice} : ${otherValue}`
                    : choice;

                let emoji = "";
                if (mapping.answers) {
                  for (const [answerKey, answerValue] of Object.entries(mapping.answers)) {
                    if (normalize(answerKey) === normChoice) {
                      emoji = answerValue;
                      break;
                    }
                  }
                }

                const showGreen = emoji === "🟢";
                const showRed = emoji === "🔴";
                const axes = mapping.axes || [1, 2, 3, 4];

                return (
                  <tr key={`choice-${sectionIdx}-${qIdx}-${cIdx}`}>
                    {[1, 2, 3, 4].map(axis => (
                      <td key={`axis-${axis}`} className="td-score axis-scoring">
                        {isSelected && axes.includes(axis) && showGreen && (
                          <img src={GOMMET_GREEN} alt="🟢" className="gomet-icon" style={customStyles2025.gommetIcon} />
                        )}
                        {isSelected && axes.includes(axis) && showRed && (
                          <img src={GOMMET_RED} alt="🔴" className="gomet-icon" style={customStyles2025.gommetIcon} />
                        )}
                      </td>
                    ))}

                    <td
                      className="answered"
                      style={{
                        color: isSelected ? "#0A93D2" : "#000",
                        fontWeight: isSelected ? "bold" : "normal"
                      }}
                    >
                      {`${cIdx + 1}. ${label}`}
                    </td>
                  </tr>
                );
              });

              const explanation = (() => {
                let explanationText = "";
                let redIsSelected = false;

                if (mapping.answers) {
                  for (const choice of question.choices) {
                    const normChoice = normalize(choice);
                    const isSelected = normalizedSelected.includes(normChoice);

                    if (isSelected && mapping.answers[choice] === "🔴") {
                      redIsSelected = true;
                      break;
                    }
                  }
                }

                if (redIsSelected && mapping.explanation) {
                  explanationText = mapping.explanation.trim();
                }

                if (!explanationText) return null;

                return (
                  <tr key={`choice-expl-${sectionIdx}-${qIdx}`}>
                    <td></td><td></td><td></td><td></td>
                    <td
                      style={{
                        color: "#D32F2F",
                        fontSize: "12px",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                        fontStyle: "italic"
                      }}
                    >
                      {explanationText}
                    </td>
                  </tr>
                );
              })();

              return (
                <React.Fragment key={`choices-frag-${sectionIdx}-${qIdx}`}>
                  {rows}
                  {explanation}
                </React.Fragment>
              );
            };

            const renderFreeAnswerRow = () => {
              if (question.choices) return null

              const freeVal = selectedValues.join(", ")
              if (!freeVal) return null

              if (mapping.numericRule) {
                const num = parseFloat(freeVal.replace(",", "."))
                const { greaterThan, emojiIfTrue, emojiIfFalse } = mapping.numericRule

                const emoji = !isNaN(num)
                  ? num > greaterThan
                    ? emojiIfTrue
                    : emojiIfFalse
                  : ""

                const axes = mapping.axes || [1, 2, 3, 4]
                const showGreen = emoji === "🟢"
                const showRed = emoji === "🔴"

                return (
                  <React.Fragment key={`q-free-${sectionIdx}-${qIdx}`}>
                    <tr key={`free-${sectionIdx}-${qIdx}`}>
                      {[1, 2, 3, 4].map(axis => (
                        <td
                          key={`axis-${axis}`}
                          className="td-score axis-scoring"
                        >
                          {axes.includes(axis) && showGreen && (
                            <img
                              src={GOMMET_GREEN}
                              alt="🟢"
                              className="gomet-icon"
                              style={customStyles2025.gommetIcon}
                            />
                          )}
                          {axes.includes(axis) && showRed && (
                            <img
                              src={GOMMET_RED}
                              alt="🔴"
                              className="gomet-icon"
                              style={customStyles2025.gommetIcon}
                            />
                          )}
                        </td>
                      ))}
                      <td
                        className="answered"
                        style={{
                          color: "#0A93D2",
                          fontWeight: "bold"
                        }}
                      >
                        {freeVal}
                      </td>
                    </tr>
                    {showRed && mapping.explanation && (
                      <tr key={`free-expl-${sectionIdx}-${qIdx}`}>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td
                          style={{
                            color: "#D32F2F",
                            fontSize: "12px",
                            paddingTop: "4px",
                            paddingBottom: "8px",
                            fontStyle: "italic"
                          }}
                        >
                          {mapping.explanation.trim()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              }

              return (
                <tr key={`q-free-${sectionIdx}-${qIdx}`}>
                  <td className="td-score axis-scoring"></td>
                  <td className="td-score axis-scoring"></td>
                  <td className="td-score axis-scoring"></td>
                  <td className="td-score axis-scoring"></td>
                  <td className="answered">{freeVal}</td>
                </tr>
              )
            }

            const matrixRows = renderMatrix() || [];

            const choiceRows = renderChoiceRows()
              ? [renderChoiceRows()]
              : [];

            const freeRows = renderFreeAnswerRow()
              ? [renderFreeAnswerRow()]
              : [];

            const body = [
              questionTitleRow,
              ...(question.type === "control_matrix"
                ? matrixRows
                : [...choiceRows, ...freeRows])
            ];

            return (
              <React.Fragment key={`q-${sectionIdx}-${qIdx}`}>
                {body}
              </React.Fragment>
            )
          })
          .filter(Boolean)

        if (!rowsForSection.length) return null

        return (
          <React.Fragment key={`section-${sectionIdx}`}>
            {section.section && (
              <tr className="tr-theme">
                <td colSpan={5}>
                  <span>{section.section}</span>
                </td>
              </tr>
            )}
            {rowsForSection}
          </React.Fragment>
        )
      })
      .filter(Boolean)
  }

  return (
    <div ref={contentRef} className="print-container">
      <style>
        {`
        .report-2025 .axis-scoring img.gomet-icon {
          width: 12px !important;
          height: 12px !important;
        }
      `}
      </style>
      <div className="page">
        <div className="text-center">
          <img
            src="/logoDigiscore.svg"
            alt="Logo Digiscore"
            className="header__logo"
          />
        </div>
        <br />
        <br />
        <h1 className="text-center">
          {t("report.title")}
          <br />
        </h1>
        <br />
        <br />
        <span className="color-blue subtitle">
          {enterprise.name}
        </span>
        <span className="textSubtitle">
          {t("report.etablishedByAdn", { year: 2025 })}
          <br />
          <br />
          {t("report.surveyCompleted")}
          {t("report.surveyCompletedBy", { name: "" })}
          <span className="textEmail">
            {session.user?.email}
          </span>{" "}
          le{" "}
          {new Date(
            session.completedAt
          ).toLocaleDateString()}
        </span>
        <br />
        <br />
        <br />
        <br />
        <br />
        <div className="score-container">
          <table
            cellPadding="0"
            cellSpacing="0"
            className="score-table"
          >
            <tbody>
              <tr>
                <td style={{ fontSize: "1.2rem" }}>
                  <div
                    style={{
                      height: "48px",
                      lineHeight: "48px",
                      fontWeight: "700",
                      textShadow: "0 0 0.6px #000",
                      fontSize: "21px"
                    }}
                  >
                    {t("report.globalScore")}
                  </div>
                </td>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "1.2rem"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid #52c3f1",
                      borderRadius: "50%",
                      width: "64px",
                      height: "64px",
                      textAlign: "center",
                      margin: "0 auto",
                      fontWeight: "700",
                      fontSize: "22px",
                      textShadow: "0 0 0.6px #000"
                    }}
                  >
                    {session.globalScore}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: ".5rem 0 .5rem 0",
                    color: "#52c3f1",
                    fontSize: "16px",
                    fontWeight: "600"
                  }}
                >
                  {t("report.scoreDistribution")} :
                </td>
              </tr>

              <tr className="distribution-cellTr">
                <td className="distribution-cell">
                  {session.namespace === "BaaS20E"
                    ? t("report.infrastructureTitle")
                    : t("result.content.chapter1.li1.title")}
                </td>
                <td className="distribution-cell-score">
                  {session.infrastructure}
                </td>
              </tr>

              <tr className="distribution-cellTr">
                <td className="distribution-cell">
                  {session.namespace === "BaaS20E"
                    ? t("report.humanResourcesTitle")
                    : t("result.content.chapter1.li2.title")}
                </td>
                <td className="distribution-cell-score">
                  {session.humanResources}
                </td>
              </tr>

              <tr className="distribution-cellTr">
                <td className="distribution-cell">
                  {session.namespace === "BaaS20E"
                    ? t("report.processFlowTitle")
                    : t("result.content.chapter1.li3.title")}
                </td>
                <td className="distribution-cell-score">
                  {session.processFlow}
                </td>
              </tr>

              <tr className="distribution-cellTr">
                <td className="distribution-cell">
                  {session.namespace === "BaaS20E"
                    ? t("report.salesProductsTitle")
                    : t("result.content.chapter1.li4.title")}
                </td>
                <td className="distribution-cell-score">
                  {session.salesProducts}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="multipages">
        {session.namespace === "BaaS20E" ? (
          <>
            <h1>{t("result.content.title")}</h1>
            <br />
            <p>{t("report.howToRead")}</p>
          </>
        ) : (
          <HowToRead />
        )}
      </div>

      <div className="force-new-page report-2025">
        <table
          className="response-table"
          style={customStyles2025.responseTable}
        >
          <thead>
            <tr>
              <th
                className="th-score vertical-header"
                style={customStyles2025.thScore}
              >
                1 - {t("result.content.chapter1.li1.title")}
              </th>
              <th
                className="th-score vertical-header"
                style={customStyles2025.thScore}
              >
                2 - {t("result.content.chapter1.li2.title")}
              </th>
              <th
                className="th-score vertical-header"
                style={customStyles2025.thScore}
              >
                3 - {t("result.content.chapter1.li3.title")}
              </th>
              <th
                className="th-score vertical-header"
                style={customStyles2025.thScore}
              >
                4 - {t("result.content.chapter1.li4.title")}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>{renderResponses()}</tbody>
        </table>
      </div>

      <br />
      <br />
      <br />
      <div className="multipages">
        <RessourcesPage />
      </div>
    </div>
  )
}

export default Report2025